// WYHSIAR GAMES WORLD - P2P WebRTC Multi-Device Networking Bridge
// Allows multiple devices (phones, tablets, PCs) to join the same room on static hosts (Cloudflare Pages)
// without requiring any backend server!

import { Peer } from 'peerjs';
import { LudoEngine } from '../server/gameEngines/ludoEngine.js';
import { PoolEngine } from '../server/gameEngines/poolEngine.js';
import { SoccerEngine } from '../server/gameEngines/soccerEngine.js';
import { BombTagEngine } from '../server/gameEngines/bombTagEngine.js';
import { BattleshipEngine } from '../server/gameEngines/battleshipEngine.js';
import { AirHockeyEngine } from '../server/gameEngines/airHockeyEngine.js';
import { ArcheryEngine } from '../server/gameEngines/archeryEngine.js';
import { TriviaEngine } from '../server/gameEngines/triviaEngine.js';
import { KartEngine } from '../server/gameEngines/kartEngine.js';
import { SnakeEngine } from '../server/gameEngines/snakeEngine.js';
import { TankEngine } from '../server/gameEngines/tankEngine.js';
import { TicTacToeEngine } from '../server/gameEngines/tictactoeEngine.js';
import { Connect4Engine } from '../server/gameEngines/connect4Engine.js';
import { PongEngine } from '../server/gameEngines/pongEngine.js';
import { UnoEngine } from '../server/gameEngines/unoEngine.js';
import { SpyEngine } from '../server/gameEngines/spyEngine.js';
import { WordBombEngine } from '../server/gameEngines/wordBombEngine.js';
import { TetrisEngine } from '../server/gameEngines/tetrisEngine.js';
import { SpaceEngine } from '../server/gameEngines/spaceEngine.js';
import { DrawGuessEngine } from '../server/gameEngines/drawGuessEngine.js';

export function generateRandomRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `WP-${code}`;
}

export class P2PNetworkBridge {
  constructor(playerName = 'Player', avatar = '🦊') {
    this.playerName = playerName;
    this.avatar = avatar;
    this.isHost = false;
    this.roomCode = null;
    this.room = null;
    this.peer = null;
    this.connections = new Map(); // peerId -> DataConnection (for host)
    this.hostConnection = null;   // DataConnection to host (for guest)
    this.listeners = new Map();   // event -> [callbacks]
    this.engineInstance = null;
    this.myId = `peer_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    this.botNames = [
      '🤖 CyberBot', '🤖 RoboChamp', '🦊 StarFox', '🐼 PandaX', '🐯 TigerPro',
      '🐸 PixelFrog', '👽 AlienAce', '🐙 OctoNinja', '🦁 LeoKnight', '🐻 BearGamer',
      '🦄 MysticHorn', '🐉 BlazeDrake', '🦅 SkyHawk', '🐺 ShadowWolf', '🐬 AquaDolphin',
      '🐰 TurboBunny', '🐵 ApeLegend', '🦖 DinoRex', '🦹 PhantomX', '⚡ VoltRider'
    ];
    this.botAvatars = [
      '🤖', '🦊', '🐼', '🐯', '🐸', '👽', '🐙', '👾', '🦁', '🐻',
      '🦄', '🐉', '🦅', '🐺', '🐬', '🐰', '🐵', '🦖', '🦹', '⚡'
    ];
    this.playerColors = [
      '#eab308', '#38bdf8', '#22c55e', '#ef4444', '#a855f7',
      '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#e11d48',
      '#6366f1', '#14b8a6', '#f43f5e', '#8b5cf6', '#d97706',
      '#10b981', '#0ea5e9', '#d946ef', '#f59e0b', '#64748b'
    ];

    // Mock IO interface for running server game engines on the Host
    this.io = {
      to: (targetId) => ({
        emit: (event, data) => {
          this.routeEngineEvent(targetId, event, data);
        }
      })
    };
  }

  get id() {
    return this.myId;
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    if (!callback) {
      this.listeners.delete(event);
    } else {
      const arr = this.listeners.get(event).filter(cb => cb !== callback);
      this.listeners.set(event, arr);
    }
  }

  trigger(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => {
        try { cb(data); } catch (e) { console.error(`Error in P2P event [${event}]:`, e); }
      });
    }
  }

  // ==========================================
  // 1. HOST CREATES ROOM
  // ==========================================
  createRoom(initialGameId = 'drawGuess', customSettings = {}) {
    return new Promise((resolve, reject) => {
      this.isHost = true;
      this.roomCode = generateRandomRoomCode();
      const peerId = `wyhsiar-${this.roomCode.toLowerCase()}`;
      const targetPlayers = customSettings.targetPlayers || 4;

      this.room = {
        code: this.roomCode,
        hostId: this.myId,
        currentGame: initialGameId,
        maxPlayers: customSettings.maxPlayers || Math.max(targetPlayers, 20),
        settings: {
          roundTimer: customSettings.roundTimer || 60,
          totalRounds: customSettings.totalRounds || 3,
          targetPlayers: targetPlayers,
          fillBots: customSettings.fillBots !== undefined ? customSettings.fillBots : false
        },
        players: [
          {
            id: this.myId,
            name: this.playerName,
            avatar: this.avatar,
            color: this.playerColors[0],
            isHost: true,
            isReady: true,
            isBot: false,
            seatIndex: 0,
            score: 0
          }
        ]
      };

      try {
        this.peer = new Peer(peerId, {
          debug: 1
        });

        this.peer.on('open', (id) => {
          this.myId = id;
          if (this.room) {
            this.room.hostId = id;
            if (this.room.players[0]) this.room.players[0].id = id;
          }
          console.log(`📡 P2P Room Host registered with PeerID: ${id}`);
          this.trigger('roomJoined', { room: this.room, isHost: true });
          resolve(this.room);
        });

        this.peer.on('connection', (conn) => {
          this.setupHostConnection(conn);
        });

        this.peer.on('error', (err) => {
          console.warn('⚠️ P2P Host peer error:', err);
          // If ID taken, generate another code and retry once
          if (err.type === 'unavailable-id') {
            this.roomCode = generateRandomRoomCode();
            const newPeerId = `wyhsiar-${this.roomCode.toLowerCase()}`;
            this.room.code = this.roomCode;
            this.peer = new Peer(newPeerId);
            this.peer.on('open', (id) => {
              this.myId = id;
              if (this.room) {
                this.room.hostId = id;
                if (this.room.players[0]) this.room.players[0].id = id;
              }
              this.trigger('roomJoined', { room: this.room, isHost: true });
              resolve(this.room);
            });
            this.peer.on('connection', (c) => this.setupHostConnection(c));
          } else {
            // Still allow local gameplay even if peer broker is blocked
            this.trigger('roomJoined', { room: this.room, isHost: true });
            resolve(this.room);
          }
        });
      } catch (e) {
        console.error('PeerJS init failed, falling back to local mode:', e);
        this.trigger('roomJoined', { room: this.room, isHost: true });
        resolve(this.room);
      }
    });
  }

  setupHostConnection(conn) {
    console.log(`🤝 Incoming connection from guest: ${conn.peer}`);

    conn.on('open', () => {
      this.connections.set(conn.peer, conn);
    });

    conn.on('data', (packet) => {
      this.handleGuestPacket(conn, packet);
    });

    conn.on('close', () => {
      console.log(`🔌 Guest disconnected: ${conn.peer}`);
      this.connections.delete(conn.peer);
      this.removePlayer(conn.peer);
    });
  }

  handleGuestPacket(conn, packet) {
    if (!packet || !packet.type) return;

    if (packet.type === 'join') {
      const limit = this.room.settings?.targetPlayers || this.room.maxPlayers || 20;
      if (this.room.players.length >= limit || this.room.players.length >= this.room.maxPlayers) {
        conn.send({ type: 'joinError', message: `Room is full (${this.room.players.length}/${limit} players)!` });
        return;
      }

      const seatIndex = this.room.players.length;

      const newPlayer = {
        id: conn.peer,
        name: packet.name || `Player ${seatIndex + 1}`,
        avatar: packet.avatar || '🐼',
        color: this.playerColors[seatIndex % this.playerColors.length],
        isHost: false,
        isReady: true,
        isBot: false,
        seatIndex,
        score: 0
      };

      this.room.players.push(newPlayer);

      // Send confirmation to joining player
      conn.send({ type: 'roomJoined', room: this.room, isHost: false });

      // Broadcast updated room to all participants
      this.broadcastToGuests({ type: 'roomUpdated', room: this.room });
      this.trigger('roomUpdated', { room: this.room });
    } else if (packet.type === 'chat') {
      const msg = {
        id: `msg_${Date.now()}`,
        senderName: packet.name,
        avatar: packet.avatar,
        text: packet.text,
        color: '#38bdf8',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      this.broadcastToGuests({ type: 'chatMessage', message: msg });
      this.trigger('chatMessage', msg);
    } else if (packet.type === 'reaction') {
      this.broadcastToGuests({ type: 'reactionGift', emoji: packet.emoji });
      this.trigger('reactionGift', { emoji: packet.emoji });
    } else if (packet.type === 'gameAction') {
      // Forward guest gameplay action into Host's active game engine
      this.routeActionToEngine(packet.senderId || conn.peer, packet.event, packet.data);
    }
  }

  // ==========================================
  // 2. GUEST JOINS ROOM VIA CODE
  // ==========================================
  joinRoom(targetCode) {
    return new Promise((resolve, reject) => {
      this.isHost = false;
      const normalizedCode = targetCode.toUpperCase().trim();
      const hostPeerId = `wyhsiar-${normalizedCode.toLowerCase()}`;

      try {
        this.peer = new Peer({ debug: 1 });

        this.peer.on('open', (myPeerId) => {
          this.myId = myPeerId;
          console.log(`📡 Connecting to Host Room [${normalizedCode}] at: ${hostPeerId}...`);

          const conn = this.peer.connect(hostPeerId, { reliable: true });
          this.hostConnection = conn;

          conn.on('open', () => {
            console.log(`✅ Connected to Host! Sending join request...`);
            conn.send({
              type: 'join',
              name: this.playerName,
              avatar: this.avatar
            });
          });

          conn.on('data', (packet) => {
            this.handleHostPacket(packet);
          });

          conn.on('close', () => {
            console.warn('🔌 Connection to host closed.');
            this.trigger('kickedFromRoom', { message: 'Disconnected from host room.' });
          });

          conn.on('error', (err) => {
            console.error('Connection error with host:', err);
            this.trigger('joinError', { message: 'Could not connect to host. Make sure room code is correct!' });
            reject(err);
          });

          // Timeout check
          setTimeout(() => {
            if (!this.room) {
              this.trigger('joinError', { message: `Room ${normalizedCode} not found or host is offline. Verify the code!` });
            }
          }, 8000);
        });

        this.peer.on('error', (err) => {
          console.error('Peer error on guest join:', err);
          this.trigger('joinError', { message: `Room ${normalizedCode} not found. Check the code!` });
          reject(err);
        });
      } catch (e) {
        console.error('Join room failed:', e);
        this.trigger('joinError', { message: 'Failed to initialize connection.' });
        reject(e);
      }
    });
  }

  handleHostPacket(packet) {
    if (!packet || !packet.type) return;

    if (packet.type === 'roomJoined') {
      this.room = packet.room;
      this.trigger('roomJoined', { room: packet.room, isHost: false });
    } else if (packet.type === 'roomUpdated') {
      this.room = packet.room;
      this.trigger('roomUpdated', { room: packet.room });
    } else if (packet.type === 'joinError') {
      this.trigger('joinError', { message: packet.message });
    } else if (packet.type === 'kickedFromRoom') {
      this.trigger('kickedFromRoom', { message: packet.message });
    } else if (packet.type === 'gameLaunched') {
      this.room = packet.room;
      this.trigger('gameLaunched', { gameId: packet.gameId, room: packet.room });
    } else if (packet.type === 'returnedToLobby') {
      this.room = packet.room;
      this.trigger('returnedToLobby', { room: packet.room });
    } else if (packet.type === 'gameEvent') {
      this.trigger(packet.event, packet.data);
    } else if (packet.type === 'chatMessage') {
      this.trigger('chatMessage', packet.message);
    } else if (packet.type === 'reactionGift') {
      this.trigger('reactionGift', { emoji: packet.emoji });
    }
  }

  // ==========================================
  // 3. HOST ROOM MANAGEMENT (Bots, Kicks, Settings)
  // ==========================================
  addBot() {
    if (!this.isHost || !this.room) return;
    const limit = this.room.settings?.targetPlayers || this.room.maxPlayers || 20;
    if (this.room.players.length >= limit || this.room.players.length >= this.room.maxPlayers) return;

    const seatIndex = this.room.players.length;
    const botIdx = Math.floor(Math.random() * this.botNames.length);

    const newBot = {
      id: `bot_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name: this.botNames[botIdx] || `🤖 Bot ${seatIndex + 1}`,
      avatar: this.botAvatars[botIdx] || '🤖',
      color: this.playerColors[seatIndex % this.playerColors.length],
      isHost: false,
      isReady: true,
      isBot: true,
      seatIndex,
      score: 0
    };

    this.room.players.push(newBot);
    this.broadcastToGuests({ type: 'roomUpdated', room: this.room });
    this.trigger('roomUpdated', { room: this.room });
  }

  removePlayer(targetId) {
    if (!this.isHost || !this.room) return;
    const idx = this.room.players.findIndex(p => p.id === targetId);
    if (idx !== -1 && !this.room.players[idx].isHost) {
      const removed = this.room.players.splice(idx, 1)[0];
      this.room.players.forEach((p, i) => { p.seatIndex = i; });

      // If removed was a connected guest, notify them
      const conn = this.connections.get(targetId);
      if (conn) {
        try {
          conn.send({ type: 'kickedFromRoom', message: 'You were removed from the room by the Host.' });
          conn.close();
        } catch (e) {}
        this.connections.delete(targetId);
      }

      this.broadcastToGuests({ type: 'roomUpdated', room: this.room });
      this.trigger('roomUpdated', { room: this.room });
    }
  }

  broadcastToGuests(packet) {
    for (const conn of this.connections.values()) {
      try {
        if (conn.open) conn.send(packet);
      } catch (e) {
        console.error('Error broadcasting to guest:', e);
      }
    }
  }

  routeEngineEvent(targetId, event, data) {
    const isRoomBroadcast = !targetId || targetId === this.room?.code;

    if (isRoomBroadcast) {
      // 1. Trigger locally on Host
      this.trigger(event, data);

      // 2. Broadcast to all connected guests over DataChannel
      if (this.isHost) {
        this.broadcastToGuests({
          type: 'gameEvent',
          event,
          data
        });
      }
    } else if (String(targetId).toLowerCase() === String(this.myId).toLowerCase()) {
      // Private event targeted specifically to Host!
      this.trigger(event, data);
    } else {
      // Private event targeted specifically to a Guest!
      const conn = this.connections.get(targetId);
      if (conn?.open) {
        conn.send({
          type: 'gameEvent',
          event,
          data
        });
      }
    }
  }

  broadcastGameEvent(event, data) {
    this.routeEngineEvent(null, event, data);
  }

  // ==========================================
  // 4. UNIFIED EMIT INTERFACE (Works like Socket.io)
  // ==========================================
  emit(event, data = {}) {
    // If guest, forward action to host
    if (!this.isHost) {
      if (this.hostConnection?.open) {
        if (event === 'sendChatMessage') {
          this.hostConnection.send({ type: 'chat', text: data.text, name: this.playerName, avatar: this.avatar });
        } else if (event === 'sendReaction') {
          this.hostConnection.send({ type: 'reaction', emoji: data.emoji });
        } else {
          this.hostConnection.send({
            type: 'gameAction',
            senderId: this.myId,
            event,
            data
          });
        }
      }
      return;
    }

    // HOST ACTIONS
    if (event === 'addBot') {
      this.addBot();
      return;
    }

    if (event === 'removePlayer') {
      this.removePlayer(data.targetId);
      return;
    }

    if (event === 'updateRoomSettings') {
      if (this.room) {
        this.room.settings = { ...this.room.settings, ...data };
        this.broadcastToGuests({ type: 'roomUpdated', room: this.room });
        this.trigger('roomUpdated', { room: this.room });
      }
      return;
    }

    if (event === 'selectAndLaunchGame') {
      const gameId = data.gameId || 'drawGuess';
      this.startHostGameEngine(gameId, this.room?.settings);
      this.broadcastToGuests({ type: 'gameLaunched', gameId, room: this.room });
      this.trigger('gameLaunched', { gameId, room: this.room });
      return;
    }

    if (event === 'returnToLobby') {
      if (this.engineInstance?.destroy) {
        this.engineInstance.destroy();
        this.engineInstance = null;
      }
      if (this.room) {
        this.room.currentGame = null;
        this.broadcastToGuests({ type: 'returnedToLobby', room: this.room });
        this.trigger('returnedToLobby', { room: this.room });
      }
      return;
    }

    if (event === 'sendChatMessage') {
      const msg = {
        id: `msg_${Date.now()}`,
        senderName: this.playerName,
        avatar: this.avatar,
        text: data.text,
        color: '#f59e0b',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      this.broadcastToGuests({ type: 'chatMessage', message: msg });
      this.trigger('chatMessage', msg);
      return;
    }

    if (event === 'sendReaction') {
      this.broadcastToGuests({ type: 'reactionGift', emoji: data.emoji });
      this.trigger('reactionGift', { emoji: data.emoji });
      return;
    }

    // Direct game action from Host into Engine
    this.routeActionToEngine(this.myId, event, data);
  }

  routeActionToEngine(senderId, event, data) {
    if (!this.engineInstance) return;

    // Ludo
    if (event === 'ludo_roll') this.engineInstance.rollDice?.(senderId);
    else if (event === 'ludo_move') this.engineInstance.moveToken?.(senderId, data.tokenIndex);
    else if (event === 'ludo_setMatchSize') this.engineInstance.setMatchSize?.(data.matchSize);
    else if (event === 'ludo_customize') this.engineInstance.customizePlayer?.(senderId, data);
    else if (event === 'ludo_enterGame') this.engineInstance.enterGame?.(senderId);
    else if (event === 'ludo_leaveMatch') this.engineInstance.handleLeaveMatch?.(senderId);

    // Pool
    else if (event === 'pool_shoot') this.engineInstance.shoot?.(senderId, data.angle, data.power);

    // Soccer
    else if (event === 'soccer_input') this.engineInstance.handleInput?.(senderId, data);

    // Bomb Tag
    else if (event === 'bt_input') this.engineInstance.handleInput?.(senderId, data);

    // Battleship
    else if (event === 'bs_strike') this.engineInstance.fireStrike?.(senderId, data.row, data.col);

    // Air Hockey
    else if (event === 'hockey_paddle') this.engineInstance.handlePaddle?.(senderId, data.x, data.y);

    // Archery
    else if (event === 'archery_shoot') this.engineInstance.shootArrow?.(senderId, data.angle, data.power);

    // Trivia
    else if (event === 'trivia_chooseCategory') this.engineInstance.setCategoryAndBegin?.(data.category);
    else if (event === 'trivia_answer') this.engineInstance.submitAnswer?.(senderId, data.choiceIndex);

    // Kart
    else if (event === 'kart_input') this.engineInstance.handleInput?.(senderId, data);

    // Snake
    else if (event === 'snake_steer') this.engineInstance.handleInput?.(senderId, data.angle);
    else if (event === 'snake_respawn') this.engineInstance.respawnSnake?.(senderId);

    // Tank
    else if (event === 'tank_input') this.engineInstance.handleInput?.(senderId, data);
    else if (event === 'tank_turret') this.engineInstance.handleTurret?.(senderId, data.angle);
    else if (event === 'tank_fire') this.engineInstance.fireBullet?.(senderId);

    // Tic-Tac-Toe
    else if (event === 'ttt_move') this.engineInstance.makeMove?.(senderId, data.cellIndex);

    // Connect 4
    else if (event === 'c4_drop') this.engineInstance.dropPiece?.(senderId, data.column);

    // Cyber Pong
    else if (event === 'pong_paddle') this.engineInstance.handlePaddle?.(senderId, data.y);

    // Uno
    else if (event === 'uno_play') this.engineInstance.playCard?.(senderId, data.cardIndex, data.wildColor);
    else if (event === 'uno_draw') this.engineInstance.drawCard?.(senderId);

    // Spy
    else if (event === 'spy_speak') this.engineInstance.submitDescription?.(senderId, data.text);
    else if (event === 'spy_vote') this.engineInstance.castVote?.(senderId, data.targetId);

    // Word Bomb
    else if (event === 'wb_submit') this.engineInstance.submitWord?.(senderId, data.word);

    // Tetris
    else if (event === 'tetris_updateBoard') this.engineInstance.handleBoardUpdate?.(senderId, data.board, data.linesCleared);
    else if (event === 'tetris_dead') this.engineInstance.handlePlayerDeath?.(senderId);

    // Space
    else if (event === 'space_input') this.engineInstance.handleInput?.(senderId, data);

    // Draw & Guess
    else if (event === 'dg_chooseWord') this.engineInstance.chooseWord?.(senderId, data.word);
    else if (event === 'dg_stroke') this.engineInstance.handleStroke?.(senderId, data);
    else if (event === 'dg_clear') this.engineInstance.handleClearCanvas?.(senderId);
  }

  startHostGameEngine(gameId, options = { targetPlayers: 4, fillBots: true }) {
    if (this.engineInstance?.destroy) {
      this.engineInstance.destroy();
      this.engineInstance = null;
    }

    // 20 Engines Factory
    if (gameId === 'ludoParty') this.engineInstance = new LudoEngine(this.io, this.room);
    else if (gameId === 'poolGame') this.engineInstance = new PoolEngine(this.io, this.room);
    else if (gameId === 'soccerGame') this.engineInstance = new SoccerEngine(this.io, this.room);
    else if (gameId === 'bombTag') this.engineInstance = new BombTagEngine(this.io, this.room);
    else if (gameId === 'battleship') this.engineInstance = new BattleshipEngine(this.io, this.room);
    else if (gameId === 'airHockey') this.engineInstance = new AirHockeyEngine(this.io, this.room);
    else if (gameId === 'archeryClash') this.engineInstance = new ArcheryEngine(this.io, this.room);
    else if (gameId === 'triviaMania') this.engineInstance = new TriviaEngine(this.io, this.room);
    else if (gameId === 'kartRacers') this.engineInstance = new KartEngine(this.io, this.room);
    else if (gameId === 'snakeArena') this.engineInstance = new SnakeEngine(this.io, this.room);
    else if (gameId === 'tankArena') this.engineInstance = new TankEngine(this.io, this.room);
    else if (gameId === 'ticTacToe') this.engineInstance = new TicTacToeEngine(this.io, this.room);
    else if (gameId === 'connect4') this.engineInstance = new Connect4Engine(this.io, this.room);
    else if (gameId === 'cyberPong') this.engineInstance = new PongEngine(this.io, this.room);
    else if (gameId === 'unoCard') this.engineInstance = new UnoEngine(this.io, this.room);
    else if (gameId === 'spyGame') this.engineInstance = new SpyEngine(this.io, this.room);
    else if (gameId === 'wordBomb') this.engineInstance = new WordBombEngine(this.io, this.room);
    else if (gameId === 'tetrisBattle') this.engineInstance = new TetrisEngine(this.io, this.room);
    else if (gameId === 'spaceSurvival') this.engineInstance = new SpaceEngine(this.io, this.room);
    else if (gameId === 'drawGuess') this.engineInstance = new DrawGuessEngine(this.io, this.room);

    setTimeout(() => {
      if (this.engineInstance) {
        if (this.engineInstance.start) this.engineInstance.start();
        else if (this.engineInstance.startRound) this.engineInstance.startRound();
      }
    }, 100);

    return this.room;
  }

  destroy() {
    if (this.engineInstance?.destroy) {
      this.engineInstance.destroy();
      this.engineInstance = null;
    }
    if (this.peer) {
      try { this.peer.destroy(); } catch (e) {}
      this.peer = null;
    }
  }
}
